'use client';

import { useEffect, useRef, useState } from 'react';

import { useDebounce } from '@/react/hooks/useDebounce';

/**
 * Scroll interface. Contains x and y positions.
 */
interface IScrollPosition {
  /**
   * X position (horizontal).
   */
  scrollX: number;
  /**
   * Y position (vertical).
   */
  scrollY: number;
  /**
   * Delta X position (horizontal) - difference between current and previous scroll position X.
   * If the user scrolls to the right, deltaX will be positive.
   * If the user scrolls to the left, deltaX will be negative.
   */
  deltaX: number;
  /**
   * Delta Y position (vertical) - difference between current and previous scroll position Y
   * If the user scrolls down, deltaY will be positive.
   * If the user scrolls up, deltaY will be negative.
   */
  deltaY: number;
}

/**
 * Custom hook that returns the current scroll position and the difference between
 * the current and previous scroll position.
 * @param delay - The delay in milliseconds before the scroll position is updated.
 * @returns Returns an object with the current scroll position and deltas.
 */
export function useScroll(delay: number = 20): IScrollPosition {
  const [scrollPosition, setScrollPosition] = useState<IScrollPosition>({
    scrollX: 0,
    scrollY: 0,
    deltaX: 0,
    deltaY: 0
  });

  // Use ref to store previous scroll position to avoid stale closures
  const prevScrollPositionRef = useRef<{ scrollX: number; scrollY: number }>({
    scrollX: 0,
    scrollY: 0
  });
  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef<boolean>(true);
  const debounce = useDebounce();

  useEffect(() => {
    isMountedRef.current = true;

    /**
     * Handles scroll event and updates scroll position; debounce the scroll
     * event to prevent performance issues.
     *
     * The function is enclosed in a useEffect hook to prevent the function from
     * being recreated on every render. This allows the function to be properly
     * removed from the event listener once the component is unmounted.
     */
    const handleScroll = (): void => {
      // Use the debounce hook to schedule the scroll position update
      debounce(delay, () => {
        // Only update state if component is still mounted
        if (!isMountedRef.current) {
          return;
        }

        const { scrollX, scrollY } = window;
        const prevScroll = { scrollX: 0, scrollY: 0 };

        // Calculate deltas: current - previous
        // Positive deltaX means scrolled right, negative means scrolled left
        // Positive deltaY means scrolled down, negative means scrolled up
        const deltaX = scrollX - prevScroll.scrollX;
        const deltaY = scrollY - prevScroll.scrollY;

        setScrollPosition({
          scrollX,
          scrollY,
          deltaX: Math.floor(deltaX),
          deltaY: Math.floor(deltaY)
        });

        // Update previous position for next calculation
        prevScrollPositionRef.current = { scrollX, scrollY };
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('scroll', handleScroll);
    };
  }, [delay, debounce]);

  return scrollPosition;
}
